import {searchBellMusic} from "../MusicLibrary/Services";

// Physical Training (PT) service layer.
//
// PT tunes are reused from the school's bell-music library (the same MinIO
// backed catalogue used elsewhere). PT routines themselves are a school-local
// concept with no dedicated backend endpoint yet, so they are persisted to
// localStorage. This keeps the feature fully functional today and gives a
// single seam to swap in a REST API later.

const STORAGE_KEY = "pt_routines_v1";

/* ----------------------------- tunes ----------------------------- */

const trackUrl = (track) => track?.url || track?.uri || track?.fileUrl || track?.bell || "";
const trackName = (track) => track?.name || track?.fileName || track?.label || "Untitled";

const extractList = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.content)) return data.content;
    if (Array.isArray(data?.data)) return data.data;
    return [];
};

// Normalise the bell-music search response into the light shape the PT builder
// needs: a stable id, a display name and a playable url.
export async function fetchTunes() {
    try {
        const response = await searchBellMusic({schoolId: null, name: ""}, 1, 200);
        if (response?.status === 200) {
            return extractList(response.data)
                .map((track, index) => ({
                    id: String(track?.id ?? track?.bellMusicId ?? `${trackName(track)}-${index}`),
                    name: trackName(track),
                    type: track?.type || "CUSTOM",
                    url: trackUrl(track),
                }))
                .filter((tune) => tune.url);
        }
    } catch (e) {
        // Swallow — the caller renders an empty-state and the builder still works
        // with silent (timer-only) exercises.
    }
    return [];
}

/* ---------------------------- routines --------------------------- */

export function loadRoutines() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return [];
    }
}

export function persistRoutines(routines) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(routines));
    } catch (e) {
        // Storage may be full / disabled — nothing else we can do client-side.
    }
    return routines;
}

export function upsertRoutine(routine) {
    const routines = loadRoutines();
    const index = routines.findIndex((item) => item.id === routine.id);
    if (index >= 0) {
        routines[index] = routine;
    } else {
        routines.unshift(routine);
    }
    return persistRoutines(routines);
}

export function removeRoutine(id) {
    return persistRoutines(loadRoutines().filter((item) => item.id !== id));
}
